import { DashboardQiuckButton as defaultQuickLinkBundles, PERMISSION_GOVERNANCE_QUICK_LINKS as defaultGovernanceQuickLinks } from '../../data/dashboard/quickLinkButtonDefs';
import { hasPermission, PERM } from '../accessPermissions';
import { getOrderListPath } from '../order/orderListPath';

/** LinkContainer 会把字符串整段塞进 pathname，查询串必须拆开。 */
export function splitQuickLinkLocation(link) {
	if (typeof link !== 'string' || !link.includes('?')) return link;
	const hashAt = link.indexOf('#');
	const hash = hashAt >= 0 ? link.slice(hashAt) : '';
	const beforeHash = hashAt >= 0 ? link.slice(0, hashAt) : link;
	const queryAt = beforeHash.indexOf('?');
	return {
		pathname: beforeHash.slice(0, queryAt),
		search: beforeHash.slice(queryAt),
		...(hash ? { hash } : {}),
	};
}

/** 快捷入口种类。同一类只出现一次，订单列表的两个地址算同一类。 */
export function quickLinkKind(button) {
	const link = String(button?.link || '');
	if (link.includes('/order/create')) return 'create';
	if (link.includes('editlist')) return 'draft';
	if (link.includes('quotationlist')) return 'quotation';
	if (link.includes('performancerules')) return 'performance';
	if (link.includes('/approval/list')) return 'approval';
	if (link.includes('orderlist')) return 'orderList';
	if (link.includes('customerlist')) return 'customer';
	if (link.includes('/statistics')) return 'statistics';
	if (link.includes('productlist')) return 'product';
	if (link === '/userlist') return 'users';
	if (link.includes('role-permissions')) return 'roleTemplate';
	if (link.includes('loginlogs')) return 'loginLogs';
	return link || button?.title || '';
}

const MENU_KEY_BY_KIND = {
	create: PERM.MENU_ORDER_CREATE,
	draft: PERM.MENU_ORDER_DRAFT,
	quotation: PERM.MENU_ORDER_QUOTATION,
	orderList: PERM.MENU_ORDER_LIST,
	customer: PERM.MENU_CUSTOMER_LIST,
	statistics: PERM.MENU_STATISTICS_VIEW,
	product: PERM.MENU_PRODUCT_LIST,
	users: PERM.MENU_USER_LIST,
	roleTemplate: PERM.USER_UPDATE,
	loginLogs: PERM.MENU_LOGINLOG_LIST,
};

const buttonByTitle = (buttons, title) => (buttons || []).find((button) => button.title === title);

const bundlesFrom = (options) => options.bundles || defaultQuickLinkBundles;
const governanceFrom = (options) => options.governance || defaultGovernanceQuickLinks;

/** 角色按钮里没有、但权限点允许时补上的入口。文案用通用写法。 */
const quickLinkExtras = (bundles, governance) => [
	{ kind: 'create', button: buttonByTitle(bundles.managerBtn, '新建订单') },
	{ kind: 'draft', button: buttonByTitle(bundles.managerBtn, '订单编辑') },
	{ kind: 'quotation', button: buttonByTitle(bundles.managerBtn, '报价查询') },
	{ kind: 'orderList', button: buttonByTitle(bundles.managerBtn, '订单查询') },
	{ kind: 'performance', button: buttonByTitle(bundles.managerBtn, '绩效管理') },
	{ kind: 'customer', button: buttonByTitle(bundles.managerBtn, '客户信息') },
	{ kind: 'statistics', button: buttonByTitle(bundles.financeBtn, '业务统计') },
	{ kind: 'approval', button: buttonByTitle(bundles.managerBtn, '审批信息') },
	{ kind: 'product', button: buttonByTitle(bundles.basicBtn, '产品信息') },
	{ kind: 'users', button: governance[0] },
	{ kind: 'roleTemplate', button: governance[1] },
	{ kind: 'loginLogs', button: governance[2] },
];

const filterRoleButtons = (buttons, userInfo, canAccessRulesManagement) =>
	(buttons || []).filter((button) => {
		if (button.requiresRulesManagement && !canAccessRulesManagement) return false;
		if (button.requiresPermission && !hasPermission(userInfo, button.requiresPermission)) return false;
		return true;
	});

/** 角色按钮组只提供顺序和岗位文案；有 access 时再按权限点过滤。 */
export function resolveQuickLinkButtonsByRole(userInfo, options = {}) {
	const canAccessRulesManagement = Boolean(options.canAccessRulesManagement);
	const bundles = bundlesFrom(options);
	const governance = governanceFrom(options);
	const basicBtns = bundles.basicBtn || [];
	const roleButtonsMap = {
		admin: bundles.adminBtn || [],
		manager: bundles.managerBtn || [],
		finance: bundles.financeBtn || [],
		buyer: bundles.buyerBtn || [],
		user: bundles.userBtn || [],
		sale: bundles.saleBtn || [],
		assistant: bundles.assistantBtn || [],
	};
	const roleButtons = filterRoleButtons(roleButtonsMap[userInfo?.role] || [], userInfo, canAccessRulesManagement);
	const baseButtons = userInfo?.role === 'admin' ? roleButtons : roleButtons.length > 0 ? roleButtons.concat(basicBtns) : basicBtns;
	const existingLinks = new Set(baseButtons.map((button) => button.link));
	const permissionExtras = governance.filter(
		(button) => hasPermission(userInfo, button.requiresPermission) && !existingLinks.has(button.link),
	);
	return baseButtons.concat(permissionExtras);
}

const quickLinkAllowed = (userInfo, button, canAccessRulesManagement) => {
	if (button.requiresRulesManagement && !canAccessRulesManagement) return false;
	if (button.requiresPermission && !hasPermission(userInfo, button.requiresPermission)) return false;
	const kind = quickLinkKind(button);
	if (kind === 'performance') return canAccessRulesManagement;
	if (kind === 'approval') {
		return hasPermission(userInfo, PERM.MENU_APPROVAL_APPLICATIONS) || hasPermission(userInfo, PERM.MENU_APPROVAL_RECORDS);
	}
	const menuKey = MENU_KEY_BY_KIND[kind];
	if (menuKey) return hasPermission(userInfo, menuKey);
	return true;
};

const presentQuickLink = (userInfo, button) => {
	const next = { ...button };
	const kind = quickLinkKind(button);
	if (kind === 'orderList') {
		next.link = getOrderListPath(userInfo);
	}
	return next;
};

/**
 * 有权限快照时按权限点决定入口。
 * 角色按钮组只提供顺序和岗位文案；没有的入口补在后面，收回的权限从列表里拿掉。
 */
export function resolveQuickLinkButtons(userInfo, options = {}) {
	const canAccessRulesManagement = Boolean(options.canAccessRulesManagement);
	if (Array.isArray(userInfo?.access) && !hasPermission(userInfo, PERM.MENU_DASHBOARD_MENU)) {
		return [];
	}
	if (!Array.isArray(userInfo?.access)) return [];
	const legacy = resolveQuickLinkButtonsByRole(userInfo, options);

	const kept = legacy
		.filter((button) => quickLinkAllowed(userInfo, button, canAccessRulesManagement))
		.map((button) => presentQuickLink(userInfo, button));
	const present = new Set(kept.map(quickLinkKind));
	const extras = quickLinkExtras(bundlesFrom(options), governanceFrom(options))
		.filter((item) => item.button && !present.has(item.kind) && quickLinkAllowed(userInfo, item.button, canAccessRulesManagement))
		.map((item) => presentQuickLink(userInfo, item.button));
	return kept.concat(extras);
}
